import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { DetailSection } from '../ui/detailView';
import { useAuthContext, useHasPermission } from '../../contexts/auth';
import { useDemo } from '../../api/demo';
import { DemoState } from '../../types/tenants';
import { ERROR_SNACKBAR } from '../../consts';
import { requestTutorialAfterDemoLoad, useTutorial } from '../tutorial/tutorial.context';
import { ClearDemoModal } from './clearDemoModal.component';

/**
 * Where the demo is loaded and cleared from (Workspace > Overview). Shown while
 * the workspace holds demo data or could take it - during the trial.
 */
export const DemoWorkspaceSection = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const canManage = useHasPermission('app.change_tenant');
  const { query, start, loading } = useDemo();
  const tutorial = useTutorial();
  const [state, setState] = useState<DemoState | null>(null);
  const [clearOpened, setClearOpened] = useState(false);

  const status = currentUser?.tenant?.demo_status;

  useEffect(() => {
    query().then(setState);
  }, [query, status]);

  if (!state || (status !== 'A' && state.unavailable_reason === 'trial_over')) {
    return null;
  }

  const handleStart = async () => {
    const { success, errorData } = await start();
    if (!success) {
      enqueueSnackbar(
        errorData?.message || t('Could not load the demo data. Please try again.'),
        ERROR_SNACKBAR
      );
      return;
    }
    requestTutorialAfterDemoLoad();
    // Brings up the loading screen (DemoBanner).
    window.dispatchEvent(new Event('tenantUpdated'));
  };

  return (
    // Wrapped for the tutorial's last step, which points here - spanning the
    // grid row the section would have spanned itself.
    <Box data-tour="demo-section" sx={{ gridColumn: '1 / -1' }}>
      <DetailSection title={t('Demo data')} icon={<ScienceOutlinedIcon />} padded>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
          {status === 'A' ? (
            <>
              <Typography variant="body2">
                {t('This workspace holds demo data. E-mails go only to your workspace’s users, nothing is sent to KSeF and no courier is ordered.')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('The tutorial walks you through the app on this data. Clear it when you are ready to enter your own - the workspace goes back to how it was before the demo, keeping its users, permission groups, settings and integrations.')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={tutorial.start}>
                  {t('Start the tutorial')}
                </Button>
                {canManage && (
                  <Button variant="outlined" color="error" onClick={() => setClearOpened(true)}>
                    {t('Clear demo data')}
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2">
                {t('Try Copigo on a made-up copier service: clients, equipment, contracts, a year of settlements and invoices, and service jobs. Nothing leaves the workspace while the demo is in it, and you can clear it with one click.')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {state.unavailable_message ||
                  t('Available during the trial, in a workspace that has no records of its own yet.')}
              </Typography>
              {canManage && (
                <Button
                  variant="contained"
                  onClick={handleStart}
                  disabled={!state.can_start}
                  loading={loading}
                >
                  {t('Load demo data')}
                </Button>
              )}
            </>
          )}
        </Box>
        <ClearDemoModal opened={clearOpened} setOpened={setClearOpened} />
      </DetailSection>
    </Box>
  );
};
